import React from 'react'
import { Chat, theme } from '@/config'
import { DEFAULT_REACTIONS, MessageReactionResponse, getReactionEmoji } from '@packages/chat'
import { useChannelStateContext, useMessageContext } from 'stream-chat-react'
import { View, Text } from '@/components'
import { createStyles } from '@codeleap/styles'

export type ChatMessageReactionsProps = {
  onReaction?: (emoji: string) => void
  onReactionSent?: (reaction: MessageReactionResponse) => void
}

export const ChatMessageReactions = ({ onReactionSent, onReaction }: ChatMessageReactionsProps) => {
  const { message, isMyMessage } = useMessageContext()
  const { channel } = useChannelStateContext()

  const myReactions = message?.own_reactions || []
  const reactionCounts = message?.reaction_counts || {}

  if (Object.keys(reactionCounts).length === 0) {
    return null
  }

  async function handleReaction(emoji: string) {
    await Chat.Services.handleReaction({ message, channel, onReaction, onReactionSent, emoji })
    Promise.resolve().then(() => {
      const { APIClient } = require('@/services')
      APIClient.Chat.invalidateReactionsCache(message.id)
    })
  }

  const activeReactions = DEFAULT_REACTIONS
    .filter(reaction => {
      const reactionType = reaction?.aliases?.[0]
      return reactionCounts[reactionType] > 0
    })
    .map(reaction => ({
      emoji: reaction.emoji,
      type: reaction.aliases[0],
      count: reactionCounts[reaction.aliases[0]],
      isSelected: myReactions.some(r => r.emoji_code === reaction.emoji),
    }))

  if (activeReactions.length === 0) {
    return null
  }

  return (
    <View style={{
      ...styles.wrapper,
      alignSelf: isMyMessage() ? 'flex-end' : 'flex-start',
    }}>
      {activeReactions.map((reaction, index) => (
        <button
          key={`${reaction.emoji}-${index}`}
          onClick={() => handleReaction(reaction.emoji)}
          style={{
            ...styles.reactionButton,
            ...(reaction.isSelected ? styles.selectedReaction : {}),
          }}
          className="reaction-button"
        >
          <span style={styles.emojiText}>
            {getReactionEmoji(reaction.emoji)}
          </span>
          {reaction.count > 1 && (
            <Text
              text={String(reaction.count)}
              style={{
                fontSize: 12,
                fontWeight: '600',
                color: reaction.isSelected
                  ? theme.colors.primary
                  : theme.colors.textCaption,
              }}
            />
          )}
        </button>
      ))}
    </View>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.value(0.5),
    marginTop: theme.spacing.value(0.5),
    flexWrap: 'wrap',
  },
  reactionButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.value(0.5),
    padding: `${theme.spacing.value(0.5)}px ${theme.spacing.value(1)}px`,
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
      transform: 'scale(1.05)',
    },
  },
  selectedReaction: {
    backgroundColor: theme.colors.primaryTransparent50,
    borderColor: theme.colors.primary,
  },
  emojiText: {
    fontSize: 16,
    lineHeight: '16px',
  },
}))
